/**
 * Learner selection toggle for mobile search.
 * Allows users to filter search results by learner using a sheet/modal interface.
 */

import { Icon } from "@outschool/backpack";
import { faChevronDown, faPlus } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import React from "react";

import { useSearchByLearner } from "../../../providers/SearchByLearnerProvider";
import AddLearnerModal from "../../parentDashboardV2/components/AddLearnerModal";
import { useUpsertLearner } from "../../parentDashboardV2/hooks/useUpsertLearner";
import { SearchToggleSheet } from "../SearchToggleSheet";

/**
 * Learner selection toggle for mobile. Trigger button opening a sheet that allows selecting the active learner.
 */
export function LearnerFilterSheetToggle() {
  const { t } = useTranslation("client\\components\\search\\LearnerFilters");
  const { currentUser } = useSession();
  const { learner, setLearner } = useSearchByLearner();

  const learners = currentUser?.children || [];

  const { openModal, upsertChild, ...addLearnerModalProps } =
    useUpsertLearner();

  const handleAddLearner = async () => {
    const newLearner = await upsertChild();
    if (newLearner) {
      setLearner(newLearner);
    }
    return newLearner;
  };

  const handleLearnerSelect = (selectedLearnerUid: string) => {
    if (selectedLearnerUid === "all") {
      setLearner(null);
      return;
    }

    const selectedLearner = learners.find(
      child => child.uid === selectedLearnerUid,
    );

    if (selectedLearner) {
      setLearner(selectedLearner);
    }
  };

  // No learners - show "Add a learner" button
  if (learners.length === 0) {
    return (
      <>
        <AddLearnerModal
          {...addLearnerModalProps}
          upsertChild={handleAddLearner}
        />
        <TrackedButton
          variant="contained"
          color="primary"
          size="small"
          onClick={openModal}
          startIcon={<Icon icon={faPlus} />}
          sx={{ height: "34px", fontSize: "14px" }}
          data-test-id="learner-filter-add-learner-button"
          trackingName="learner_filter_add_learner_button"
          trackingSharedProperties={{
            location: "mobile_search",
            has_learners: false,
          }}
        >
          {t("Add a learner")}
        </TrackedButton>
      </>
    );
  }

  // Build options for the sheet (excluding "Add a learner" which is now in children)
  const sheetOptions = [
    {
      key: "all",
      label: t("All learners"),
      isSelected: learner === null,
    },
    ...learners
      .filter(c => c.name !== null)
      .map(child => ({
        key: child.uid,
        label: child.name as string,
        isSelected: learner?.uid === child.uid,
      })),
  ];

  const buttonLabel = learner?.name || t("All");

  return (
    <>
      <AddLearnerModal
        {...addLearnerModalProps}
        upsertChild={handleAddLearner}
      />
      <SearchToggleSheet
        options={sheetOptions}
        onSelect={handleLearnerSelect}
        buttonLabel={buttonLabel}
        modalTitle={t("Select Learner")}
        trackingName="learner_filter_select"
        disableRipple={true}
        buttonIcon={faChevronDown}
        buttonIconPosition="right"
        buttonSx={{
          height: "40px",
          borderRadius: "999px",
        }}
        testId="learner-filter-button"
      >
        <TrackedButton
          variant="text"
          size="small"
          onClick={openModal}
          startIcon={<Icon icon={faPlus} />}
          trackingName="learner_filter_add_learner_button"
          trackingSharedProperties={{
            location: "mobile_search_sheet",
            has_learners: true,
          }}
        >
          {t("Add a learner")}
        </TrackedButton>
      </SearchToggleSheet>
    </>
  );
}
